import { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './motion';

/* Rotating 3D market plane: location dots with beams converging on the business. */
const POINTS = Array.from({ length: 46 }, (_, index) => {
  const angle = index * 2.399;
  const radius = Math.sqrt(index / 46) * 1.05;
  return {
    x: Math.cos(angle) * radius,
    z: Math.sin(angle) * radius,
    beam: index % 4 === 0,
    strength: 0.35 + ((index * 37) % 100) / 150,
  };
});

export default function SignalMap3D() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduced = prefersReducedMotion();
    let frame;
    let angle = 0.6;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const TILT = 0.98;
    const project = (x, y, z, width, height) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const rx = x * cos - z * sin;
      const rz = x * sin + z * cos;
      const ty = y * Math.cos(TILT) - rz * Math.sin(TILT);
      const tz = y * Math.sin(TILT) + rz * Math.cos(TILT);
      const depth = tz + 3.1;
      const scale = 2.3 / depth;
      return {
        sx: width / 2 + rx * scale * width * 0.33,
        sy: height / 2 + ty * scale * height * 0.44,
        scale,
        depth,
      };
    };

    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      if (!reduced) angle += 0.0022;

      ctx.fillStyle = '#0b1230';
      ctx.fillRect(0, 0, width, height);

      // Grid rings
      for (let ring = 1; ring <= 3; ring += 1) {
        const radius = ring / 3;
        ctx.beginPath();
        for (let step = 0; step <= 48; step += 1) {
          const theta = (step / 48) * Math.PI * 2;
          const point = project(Math.cos(theta) * radius, 0, Math.sin(theta) * radius, width, height);
          if (step === 0) ctx.moveTo(point.sx, point.sy);
          else ctx.lineTo(point.sx, point.sy);
        }
        ctx.strokeStyle = 'rgba(53, 211, 240, 0.16)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Radial spokes
      for (let spoke = 0; spoke < 8; spoke += 1) {
        const theta = (spoke / 8) * Math.PI * 2;
        const inner = project(0, 0, 0, width, height);
        const outer = project(Math.cos(theta), 0, Math.sin(theta), width, height);
        ctx.beginPath();
        ctx.moveTo(inner.sx, inner.sy);
        ctx.lineTo(outer.sx, outer.sy);
        ctx.strokeStyle = 'rgba(53, 211, 240, 0.09)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      const center = project(0, 0, 0, width, height);

      // Beams from active locations to the business
      POINTS.forEach(point => {
        const base = project(point.x, 0, point.z, width, height);
        if (point.beam) {
          const top = project(point.x, -point.strength * 0.7, point.z, width, height);
          ctx.beginPath();
          ctx.moveTo(base.sx, base.sy);
          ctx.lineTo(top.sx, top.sy);
          ctx.strokeStyle = 'rgba(53, 211, 240, 0.5)';
          ctx.lineWidth = 1.3;
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(top.sx, top.sy);
          ctx.lineTo(center.sx, center.sy - 8);
          ctx.strokeStyle = 'rgba(53, 211, 240, 0.13)';
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(base.sx, base.sy, Math.max(0.8, 2.2 * base.scale), 0, Math.PI * 2);
        ctx.fillStyle = point.beam ? '#35d3f0' : 'rgba(120, 200, 245, 0.55)';
        ctx.fill();
      });

      // Business focal point
      const glow = ctx.createRadialGradient(center.sx, center.sy, 0, center.sx, center.sy, 46);
      glow.addColorStop(0, 'rgba(53, 211, 240, 0.42)');
      glow.addColorStop(1, 'rgba(53, 211, 240, 0)');
      ctx.beginPath();
      ctx.arc(center.sx, center.sy, 46, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(center.sx, center.sy, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = '#eaf4ff';
      ctx.fill();

      frame = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="signal-map" aria-hidden="true" />;
}
